/**
 * Format currency values
 * @param {number} amount - The amount to format
 * @param {string} currency - Currency symbol (default: '₦')
 * @param {number} decimals - Number of decimal places (default: 2)
 * @returns {string} Formatted currency string
 */
export const formatCurrency = (amount, currency = '₦', decimals = 2) => {
  if (amount === null || amount === undefined || Number.isNaN(amount)) {
    return `${currency}0.00`;
  }

  const formatted = Number(amount).toFixed(decimals);
  const parts = formatted.split('.');
  const integerPart = parts[0].replace(/\B(?=(\d{3})+(?!\d))/g, ',');

  return `${currency}${integerPart}${decimals > 0 ? `.${parts[1]}` : ''}`;
};

/**
 * Format percentage values
 * @param {number} value - The percentage value
 * @param {number} decimals - Number of decimal places (default: 1)
 * @returns {string} Formatted percentage string
 */
export const formatPercentage = (value, decimals = 1) => {
  if (value === null || value === undefined || Number.isNaN(value)) {
    return '0%';
  }

  return `${Number(value).toFixed(decimals)}%`;
};

/**
 * Format date values
 * @param {string|Date} date - The date to format
 * @param {string} format - Date format (default: 'DD MMM YYYY')
 * @returns {string} Formatted date string
 */
export const formatDate = (date, format = 'DD MMM YYYY') => {
  if (!date) return '';

  const dateObj = new Date(date);
  if (Number.isNaN(dateObj.getTime())) return '';

  const day = dateObj.getDate().toString().padStart(2, '0');
  const month = dateObj.toLocaleDateString('en-US', { month: 'short' });
  const year = dateObj.getFullYear();

  switch (format) {
    case 'DD MMM YYYY':
      return `${day} ${month} ${year}`;
    case 'DD/MM/YYYY':
      return `${day}/${(dateObj.getMonth() + 1).toString().padStart(2, '0')}/${year}`;
    case 'MMM DD, YYYY':
      return `${month} ${day}, ${year}`;
    default:
      return dateObj.toLocaleDateString();
  }
};

/**
 * Calculate days between two dates
 * @param {string|Date} startDate - Start date
 * @param {string|Date} endDate - End date
 * @returns {number} Number of days
 */
export const daysBetween = (startDate, endDate) => {
  const start = new Date(startDate);
  const end = new Date(endDate);
  const diffTime = Math.abs(end - start);
  return Math.ceil(diffTime / (1000 * 60 * 60 * 24));
};

/**
 * Get relative time string (e.g., "2 days ago", "in 5 days")
 * @param {string|Date} date - The date to compare
 * @returns {string} Relative time string
 */
export const getRelativeTime = (date) => {
  if (!date) return '';

  const now = new Date();
  const targetDate = new Date(date);
  const diffTime = targetDate - now;
  const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24));

  if (diffDays === 0) return 'Today';
  if (diffDays === 1) return 'Tomorrow';
  if (diffDays === -1) return 'Yesterday';
  if (diffDays > 0) return `In ${diffDays} days`;
  return `${Math.abs(diffDays)} days ago`;
};

/**
 * Truncate text to specified length
 * @param {string} text - Text to truncate
 * @param {number} maxLength - Maximum length (default: 50)
 * @returns {string} Truncated text
 */
export const truncateText = (text, maxLength = 50) => {
  if (!text || text.length <= maxLength) return text;
  return `${text.substring(0, maxLength)}...`;
};

/**
 * Parse currency string to number
 * @param {string} currencyString - Currency string to parse
 * @returns {number} Parsed number
 */
export const parseCurrency = (currencyString) => {
  if (!currencyString) return 0;

  // Remove currency symbols and commas
  const cleanString = currencyString.replace(/[₦$£€,\s]/g, '');
  const parsed = parseFloat(cleanString);

  return Number.isNaN(parsed) ? 0 : parsed;
};
